import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DD_SECTION_COUNT, SPA_SECTION_COUNT } from '../config/reportSections';

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Sprint 5 client feedback 2026-07-07 (BE Issue #6) — a friendly
     banner when the user arrives here because their session expired
     (or refresh failed) mid-work, rather than an unexplained login
     screen. Also remember the intended route so we can send them
     back after a successful re-login instead of dropping them at
     /dashboard by default. Both keys are set by api.ts's response
     interceptor before it redirects here. */
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);
  const [returnTo, setReturnTo] = useState<string | null>(null);

  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('login_reason');
      const path = sessionStorage.getItem('login_return_to');
      if (reason === 'session_expired') {
        setSessionMessage('Your session ended due to inactivity. Please sign in to continue where you left off.');
      } else if (reason === 'refresh_failed') {
        setSessionMessage('Your session has expired. Please sign in again to continue.');
      }
      if (path) setReturnTo(path);
      // Consume the flags so a manual navigation to /login later
      // doesn't re-surface the banner.
      sessionStorage.removeItem('login_reason');
      sessionStorage.removeItem('login_return_to');
    } catch {
      /* noop — sessionStorage disabled */
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const data = await apiLogin(email, password);
      const userInfo = data.user ?? {
        id: '',
        email,
        full_name: email.split('@')[0],
        role: 'analyst',
      };
      login(userInfo, data.access_token, data.refresh_token);
      // Send the user back where they were, if we captured a path
      // before the session ended. Otherwise land on the dashboard.
      navigate(returnTo ?? '/dashboard', { replace: true });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 400) {
        setError('Invalid email or password. Please try again.');
      } else if (status) {
        setError(`Server error (${status}). Please try again.`);
      } else {
        setError('Cannot reach server. Please check your connection.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-layout">
      {/* ── Left Panel ── */}
      <div className="login-left">
        {/* Sprint 5 client feedback 2026-07-03 — dropped the "DD"
            brand-mark tile (obsolete once the platform covers DD +
            SPA + Risk); "Investment Assessment Platform" renamed
            to "TSG Investment Audit AI" so the login page matches
            the tenant brand actually running. */}
        <div className="login-brand">
          <span className="login-brand-name">TSG Investment Audit AI</span>
        </div>

        <div className="login-hero">
          <div className="login-pills">
            <span className="login-pill">AI Powered</span>
            <span className="login-pill">Benchmarked</span>
            <span className="login-pill">Zero-Touch</span>
          </div>
          <h1 className="login-headline">
            Confidence-driven<br /><em>investment assessment</em>
          </h1>
          <p className="login-desc">
            Run benchmarked due diligence and SPA legal reviews on your on-premise
            infrastructure in minutes. No data leaves your network.
          </p>

          {/* Sprint 5 client feedback 2026-07-03 — dropped the
              "ISO 27001 Certified" trust item; the platform is not
              currently ISO 27001 attested and displaying the badge
              on the login page misrepresents compliance posture. */}
          <div className="login-trust">
            <div className="login-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              End-to-End Encrypted
            </div>
            <div className="login-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              On-Premise Deployment
            </div>
          </div>

          <div className="login-module-cards">
            <div className="lmc">
              <span className="lmc-chip dd">DD</span>
              <span className="lmc-name">Due Diligence</span>
              {/* Sprint 5 client feedback 2026-07-08 (UI Ticket —
                  Section counts) — sourced from shared constant so
                  future report structure changes update this copy
                  automatically. Was hardcoded "10-section" which
                  drifted from the current 12-section DD report. */}
              <span className="lmc-desc">{DD_SECTION_COUNT}-section benchmarked report</span>
            </div>
            <div className="lmc">
              <span className="lmc-chip spa">SPA</span>
              <span className="lmc-name">SPA Legal Review</span>
              <span className="lmc-desc">{SPA_SECTION_COUNT}-section clause analysis</span>
            </div>
          </div>
        </div>

        <div className="login-status">
          <div className="login-status-item">
            <span className="status-dot online" />
            System operational
          </div>
          <div className="login-status-item">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>
            Models loaded
          </div>
          <span className="login-version t-mono">v1.0.0</span>
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="login-right">
        <div className="login-form-wrap">
          <div className="login-form-card">
            <h2 className="form-card-title">Sign in</h2>
            <p className="form-card-subtitle">Welcome back. Please enter your credentials.</p>

            <form onSubmit={handleSubmit} className="login-fields">
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email</label>
                <div className="input-icon-wrap">
                  <svg className="input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <input
                    className="form-input"
                    type="email"
                    id="email"
                    placeholder="analyst@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="field-row-label">
                  <label className="form-label" htmlFor="password">Password</label>
                </div>
                <div className="input-icon-wrap pwd-wrap">
                  <svg className="input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  <input
                    className="form-input"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="pwd-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label="Toggle password visibility"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </div>
              </div>

              {/* Sprint 5 client feedback 2026-07-07 (BE Issue #6) —
                  Session-expired banner (shown once, above the login
                  error). Distinct from the credential-error alert so
                  the reviewer can tell "your session ended" from "you
                  typed the wrong password". */}
              {sessionMessage && (
                <div className="alert alert-info" role="status" style={{ marginBottom: 'var(--s3)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {sessionMessage}
                </div>
              )}
              {error && (
                <div className="alert alert-fail" role="alert">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn-signin"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="login-footer">
            Secure on-premise deployment &nbsp;·&nbsp; DD &amp; SPA Assessment Platform v1.0<br />
            Air-gapped processing — no data leaves the network
          </p>
        </div>
      </div>
    </div>
  );
}
